import { useState, useEffect, useRef, useCallback } from 'react'
import { hostingApi, hostingEnvApi, type ApiHostingProject, type ApiHostingDeployment, api } from '../utils/api'

// ── Status badge ──────────────────────────────────────────────────────────────

function StatusBadge({ status }: { status: ApiHostingDeployment['status'] }) {
  const map: Record<string, { bg: string; color: string; label: string }> = {
    pending:   { bg: '#fef3c7', color: '#92400e', label: '⏳ Ожидание' },
    building:  { bg: '#dbeafe', color: '#1e40af', label: '🔨 Сборка...' },
    success:   { bg: '#dcfce7', color: '#166534', label: '✓ Готов' },
    failed:    { bg: '#fee2e2', color: '#991b1b', label: '✕ Ошибка' },
    cancelled: { bg: '#f3f4f6', color: '#6b7280', label: '○ Отменён' },
  }
  const s = map[status] ?? map.pending
  return (
    <span style={{ fontSize: '11px', fontWeight: 700, padding: '2px 8px', borderRadius: '10px', background: s.bg, color: s.color }}>
      {s.label}
    </span>
  )
}

// ── Log viewer ────────────────────────────────────────────────────────────────

function LogViewer({ deploymentId, initialLog, status, onClose }: {
  deploymentId: string
  initialLog?: string | null
  status: ApiHostingDeployment['status']
  onClose: () => void
}) {
  const [log, setLog] = useState(initialLog ?? '')
  const bottomRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (status === 'success' || status === 'failed' || status === 'cancelled') return
    const timer = setInterval(() => {
      hostingApi.getDeployment(deploymentId)
        .then(d => { setLog(d.buildLog ?? ''); if (d.status !== 'pending' && d.status !== 'building') clearInterval(timer) })
        .catch(() => null)
    }, 2000)
    return () => clearInterval(timer)
  }, [deploymentId, status])

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' })
  }, [log])

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.7)', zIndex: 9999, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '20px' }}>
      <div style={{ background: '#0d1117', borderRadius: '12px', width: '100%', maxWidth: '860px', maxHeight: '80vh', display: 'flex', flexDirection: 'column', boxShadow: '0 24px 64px rgba(0,0,0,0.5)' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '14px 18px', borderBottom: '1px solid #30363d' }}>
          <span style={{ fontSize: '13px', fontWeight: 700, color: '#e6edf3', fontFamily: 'monospace' }}>Build Log — {deploymentId.slice(0, 8)}</span>
          <button onClick={onClose} style={{ background: 'none', border: 'none', color: '#8b949e', cursor: 'pointer', fontSize: '18px', lineHeight: 1 }}>✕</button>
        </div>
        <pre style={{
          flex: 1, overflowY: 'auto', margin: 0, padding: '16px 18px',
          fontFamily: 'monospace', fontSize: '12px', lineHeight: 1.6,
          color: '#e6edf3', whiteSpace: 'pre-wrap', wordBreak: 'break-all',
        }}>
          {log || '(Лог пока недоступен...)'}
          <div ref={bottomRef} />
        </pre>
      </div>
    </div>
  )
}

// ── Deployment row ────────────────────────────────────────────────────────────

function DeploymentRow({ d, onShowLog }: { d: ApiHostingDeployment; onShowLog: (d: ApiHostingDeployment) => void }) {
  const date = new Date(d.createdAt).toLocaleDateString('ru', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' })
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '10px 14px', background: 'var(--bg-2)', border: '1px solid var(--border)', borderRadius: '8px' }}>
      <StatusBadge status={d.status} />
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: '12px', color: 'var(--text-primary)', fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {d.commitMsg ?? `Deploy ${d.id.slice(0, 8)}`}
        </div>
        <div style={{ fontSize: '10px', color: 'var(--text-muted)', marginTop: '1px' }}>
          {d.branch}{d.commitSha ? ` · ${d.commitSha}` : ''} · {date} · {d.triggeredBy}
        </div>
      </div>
      {d.previewUrl && d.status === 'success' && (
        <a href={d.previewUrl} target="_blank" rel="noopener noreferrer"
          style={{ fontSize: '11px', padding: '4px 10px', background: 'var(--accent)', color: '#fff', borderRadius: '6px', textDecoration: 'none', fontWeight: 600, whiteSpace: 'nowrap' }}>
          Preview →
        </a>
      )}
      <button onClick={() => onShowLog(d)}
        style={{ fontSize: '11px', padding: '4px 10px', background: 'var(--bg-3)', border: '1px solid var(--border)', borderRadius: '6px', cursor: 'pointer', color: 'var(--text-muted)', whiteSpace: 'nowrap' }}>
        Логи
      </button>
    </div>
  )
}

// ── §51 Monitoring Tab ────────────────────────────────────────────────────────

interface HealthStatus {
  status: 'up' | 'down' | 'unknown'
  statusCode?: number | null
  latencyMs?: number | null
  url?: string
  deploymentId?: string
  projectName?: string
  checkedAt?: string
  message?: string
}

interface RuntimeLogs {
  deploymentId: string | null
  deploymentStatus?: string
  deploymentDate?: string
  commitMsg?: string | null
  lines: Array<{ i: number; line: string; level: 'error' | 'warn' | 'info' }>
}

// ── §17 Environments Tab ──────────────────────────────────────────────────────

const ENV_NAMES = ['production', 'preview', 'development'] as const

function EnvironmentsTab({ projectId }: { projectId: string }) {
  const [selectedEnv, setSelectedEnv] = useState<string>('production')
  const [envData, setEnvData] = useState<Array<{ id: string; name: string; keys: string[] }>>([])
  const [loading, setLoading] = useState(true)
  const [newKey, setNewKey] = useState('')
  const [newVal, setNewVal] = useState('')
  const [saving, setSaving] = useState(false)

  const reload = useCallback(() => {
    hostingEnvApi.listEnvironments(projectId)
      .then(setEnvData)
      .catch(() => null)
      .finally(() => setLoading(false))
  }, [projectId])

  useEffect(() => { reload() }, [reload])

  const currentEnv = envData.find(e => e.name === selectedEnv)

  async function addVar() {
    if (!newKey.trim() || !newVal.trim()) return
    setSaving(true)
    try {
      await hostingEnvApi.setEnvVar(projectId, selectedEnv, newKey.trim().toUpperCase(), newVal.trim())
      setNewKey(''); setNewVal('')
      reload()
    } catch { /* noop */ } finally { setSaving(false) }
  }

  async function deleteVar(key: string) {
    await hostingEnvApi.deleteEnvVar(projectId, selectedEnv, key).catch(() => null)
    reload()
  }

  return (
    <div style={{ flex: 1, overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: 12 }}>
      <div style={{ fontSize: 12, color: 'var(--text-muted)', lineHeight: 1.5 }}>
        Переменные окружения для каждого environment. Значения шифруются AES-256-GCM.
      </div>

      {/* Environment selector */}
      <div style={{ display: 'flex', gap: 6 }}>
        {ENV_NAMES.map(name => (
          <button key={name} onClick={() => setSelectedEnv(name)}
            style={{ padding: '5px 14px', borderRadius: 8, border: `1px solid ${selectedEnv === name ? 'var(--accent)' : 'var(--border)'}`, background: selectedEnv === name ? 'var(--accent)' : 'var(--bg-2)', color: selectedEnv === name ? '#fff' : 'var(--text-secondary)', fontSize: 12, fontWeight: 600, cursor: 'pointer' }}>
            {name === 'production' ? '🔴 Production' : name === 'preview' ? '🟡 Preview' : '🟢 Development'}
          </button>
        ))}
      </div>

      {/* Vars list */}
      {loading ? (
        <div style={{ color: 'var(--text-muted)', fontSize: 12 }}>Загрузка...</div>
      ) : !currentEnv || currentEnv.keys.length === 0 ? (
        <div style={{ fontSize: 12, color: 'var(--text-muted)', padding: '12px', background: 'var(--bg-2)', borderRadius: 8, textAlign: 'center' }}>
          Нет переменных для {selectedEnv}
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
          {currentEnv.keys.map(k => (
            <div key={k} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '7px 10px', background: 'var(--bg-2)', borderRadius: 8, border: '1px solid var(--border)' }}>
              <code style={{ flex: 1, fontSize: 12, color: 'var(--text-primary)', fontFamily: 'monospace' }}>{k}</code>
              <code style={{ fontSize: 11, color: 'var(--text-muted)', fontFamily: 'monospace' }}>••••••••</code>
              <button onClick={() => void deleteVar(k)}
                style={{ background: 'none', border: 'none', color: '#ef4444', cursor: 'pointer', fontSize: 13 }}>🗑</button>
            </div>
          ))}
        </div>
      )}

      {/* Add new var */}
      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
        <input
          className="prop-input"
          placeholder="KEY_NAME"
          value={newKey}
          onChange={e => setNewKey(e.target.value)}
          style={{ flex: '1 1 120px', fontFamily: 'monospace', fontSize: 12 }}
        />
        <input
          className="prop-input"
          placeholder="value"
          type="password"
          value={newVal}
          onChange={e => setNewVal(e.target.value)}
          style={{ flex: '2 1 180px', fontSize: 12 }}
        />
        <button onClick={() => void addVar()} disabled={saving || !newKey.trim() || !newVal.trim()}
          style={{ padding: '6px 14px', background: 'var(--accent)', color: '#fff', border: 'none', borderRadius: 8, fontSize: 12, fontWeight: 700, cursor: 'pointer', opacity: saving || !newKey.trim() || !newVal.trim() ? 0.6 : 1 }}>
          {saving ? '...' : '+ Добавить'}
        </button>
      </div>
    </div>
  )
}

function MonitoringTab({ projectId }: { projectId: string }) {
  const [health, setHealth] = useState<HealthStatus | null>(null)
  const [logs, setLogs] = useState<RuntimeLogs | null>(null)
  const [healthLoading, setHealthLoading] = useState(false)
  const [logsLoading, setLogsLoading] = useState(false)
  const logsEndRef = useRef<HTMLDivElement>(null)

  const checkHealth = useCallback(async () => {
    setHealthLoading(true)
    try {
      const result = await api.get<HealthStatus>(`/hosting/projects/${projectId}/health`)
      setHealth(result)
    } catch { setHealth({ status: 'unknown', message: 'Не удалось проверить' }) }
    finally { setHealthLoading(false) }
  }, [projectId])

  const loadLogs = useCallback(async () => {
    setLogsLoading(true)
    try {
      const result = await api.get<RuntimeLogs>(`/hosting/projects/${projectId}/runtime-logs?limit=200`)
      setLogs(result)
    } catch { /* noop */ }
    finally { setLogsLoading(false) }
  }, [projectId])

  useEffect(() => {
    void checkHealth()
    void loadLogs()
  }, [checkHealth, loadLogs])

  useEffect(() => {
    logsEndRef.current?.scrollIntoView({ behavior: 'smooth' })
  }, [logs])

  const healthColor = health?.status === 'up' ? '#22c55e' : health?.status === 'down' ? '#ef4444' : '#f59e0b'
  const healthLabel = health?.status === 'up' ? '✓ Работает' : health?.status === 'down' ? '✕ Недоступен' : '? Неизвестно'

  return (
    <div style={{ flex: 1, overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: '16px' }}>
      {/* Health status */}
      <div style={{ padding: '14px 16px', background: 'var(--bg-2)', border: '1px solid var(--border)', borderRadius: '12px' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
          <div style={{ fontSize: '12px', fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
            Health Check
          </div>
          <button
            onClick={() => void checkHealth()}
            disabled={healthLoading}
            style={{ fontSize: '11px', padding: '3px 10px', background: 'var(--bg-3)', border: '1px solid var(--border)', borderRadius: '6px', cursor: 'pointer', color: 'var(--text-muted)' }}
          >
            {healthLoading ? '...' : '↻ Обновить'}
          </button>
        </div>
        {health ? (
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <span style={{ width: 10, height: 10, borderRadius: '50%', background: healthColor, flexShrink: 0, boxShadow: `0 0 6px ${healthColor}` }} />
              <span style={{ fontSize: '13px', fontWeight: 700, color: healthColor }}>{healthLabel}</span>
            </div>
            {health.latencyMs != null && (
              <span style={{ fontSize: '12px', color: 'var(--text-muted)' }}>⏱ {health.latencyMs}ms</span>
            )}
            {health.statusCode != null && (
              <span style={{ fontSize: '12px', color: 'var(--text-muted)' }}>HTTP {health.statusCode}</span>
            )}
            {health.url && (
              <a href={health.url} target="_blank" rel="noopener noreferrer" style={{ fontSize: '11px', color: 'var(--accent)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: '200px' }}>
                {health.url}
              </a>
            )}
          </div>
        ) : (
          <div style={{ fontSize: '12px', color: 'var(--text-muted)' }}>Загрузка...</div>
        )}
      </div>

      {/* Runtime logs */}
      <div style={{ flex: 1, display: 'flex', flexDirection: 'column', minHeight: 0 }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 6 }}>
          <div style={{ fontSize: '12px', fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
            Логи деплоя
            {logs?.commitMsg && <span style={{ fontWeight: 400, marginLeft: 6, textTransform: 'none' }}>· {logs.commitMsg}</span>}
          </div>
          <button
            onClick={() => void loadLogs()}
            disabled={logsLoading}
            style={{ fontSize: '11px', padding: '3px 10px', background: 'var(--bg-3)', border: '1px solid var(--border)', borderRadius: '6px', cursor: 'pointer', color: 'var(--text-muted)' }}
          >
            {logsLoading ? '...' : '↻'}
          </button>
        </div>
        <div style={{
          flex: 1, overflowY: 'auto', background: '#0d1117', borderRadius: '10px',
          padding: '12px 14px', fontFamily: 'monospace', fontSize: '11px', lineHeight: 1.6,
          maxHeight: '320px', minHeight: '100px',
        }}>
          {logsLoading && <span style={{ color: '#8b949e' }}>Загрузка...</span>}
          {!logsLoading && (!logs || logs.lines.length === 0) && (
            <span style={{ color: '#8b949e' }}>(Логи недоступны)</span>
          )}
          {logs?.lines.map(l => (
            <div key={l.i} style={{
              color: l.level === 'error' ? '#f85149' : l.level === 'warn' ? '#e3b341' : '#e6edf3',
              whiteSpace: 'pre-wrap', wordBreak: 'break-all',
            }}>
              {l.line}
            </div>
          ))}
          <div ref={logsEndRef} />
        </div>
      </div>
    </div>
  )
}

// ── Project detail view ───────────────────────────────────────────────────────

function ProjectDetail({ project, onBack }: { project: ApiHostingProject; onBack: () => void }) {
  const [deployments, setDeployments] = useState<ApiHostingDeployment[]>([])
  const [loadingDeploys, setLoadingDeploys] = useState(true)
  const [deploying, setDeploying] = useState(false)
  const [logTarget, setLogTarget] = useState<ApiHostingDeployment | null>(null)
  const [envKeys, setEnvKeys] = useState<string[]>([])
  const [newEnvKey, setNewEnvKey] = useState('')
  const [newEnvVal, setNewEnvVal] = useState('')
  const [envTab, setEnvTab] = useState<'deploys' | 'env' | 'environments' | 'settings' | 'monitoring'>('deploys')
  const [editingProject, setEditingProject] = useState({ ...project })
  const [saving, setSaving] = useState(false)
  const fileRef = useRef<HTMLInputElement>(null)

  const reload = useCallback(() => {
    hostingApi.listDeployments(project.id).then(d => setDeployments(d)).catch(() => null).finally(() => setLoadingDeploys(false))
  }, [project.id])

  useEffect(() => { reload() }, [reload])
  useEffect(() => {
    hostingApi.getEnvKeys(project.id).then(setEnvKeys).catch(() => null)
  }, [project.id])

  // Poll while building
  useEffect(() => {
    const building = deployments.some(d => d.status === 'pending' || d.status === 'building')
    if (!building) return
    const t = setTimeout(reload, 3000)
    return () => clearTimeout(t)
  }, [deployments, reload])

  async function deployGit() {
    if (deploying) return
    setDeploying(true)
    try {
      await hostingApi.deployGit(project.id)
      reload()
    } catch (e) { alert(String(e)) } finally { setDeploying(false) }
  }

  async function deployZip(file: File) {
    if (deploying) return
    setDeploying(true)
    try {
      await hostingApi.deployZip(project.id, file)
      reload()
    } catch (e) { alert(String(e)) } finally { setDeploying(false) }
  }

  async function addEnvVar() {
    if (!newEnvKey.trim()) return
    await hostingApi.setEnvVar(project.id, newEnvKey.trim(), newEnvVal).catch(e => alert(String(e)))
    setEnvKeys(k => k.includes(newEnvKey.trim()) ? k : [...k, newEnvKey.trim()])
    setNewEnvKey(''); setNewEnvVal('')
  }

  async function removeEnvVar(key: string) {
    await hostingApi.deleteEnvVar(project.id, key).catch(() => null)
    setEnvKeys(k => k.filter(x => x !== key))
  }

  async function saveSettings() {
    setSaving(true)
    const patch = {
      ...editingProject,
      repoUrl: editingProject.repoUrl ?? undefined,
    }
    await hostingApi.updateProject(project.id, patch).catch(e => alert(String(e))).finally(() => setSaving(false))
  }

  const tabStyle = (active: boolean): React.CSSProperties => ({
    padding: '6px 14px', fontSize: '12px', fontWeight: 700, cursor: 'pointer',
    background: 'none', border: 'none', borderBottom: `2px solid ${active ? 'var(--accent)' : 'transparent'}`,
    color: active ? 'var(--accent)' : 'var(--text-muted)',
  })

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%', gap: '0' }}>
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '0 0 16px' }}>
        <button onClick={onBack} style={{ background: 'none', border: '1px solid var(--border)', borderRadius: '8px', padding: '5px 10px', fontSize: '12px', cursor: 'pointer', color: 'var(--text-muted)' }}>← Назад</button>
        <h2 style={{ margin: 0, fontSize: '18px', fontWeight: 800, color: 'var(--text-primary)' }}>{project.name}</h2>
        <span style={{ fontSize: '11px', padding: '2px 8px', background: 'var(--bg-3)', borderRadius: '8px', color: 'var(--text-muted)' }}>{project.framework}</span>
      </div>

      {/* Tab bar */}
      <div style={{ display: 'flex', borderBottom: '1px solid var(--border)', marginBottom: '16px', overflowX: 'auto' }}>
        <button style={tabStyle(envTab === 'deploys')} onClick={() => setEnvTab('deploys')}>🚀 Деплои</button>
        <button style={tabStyle(envTab === 'monitoring')} onClick={() => setEnvTab('monitoring')}>📊 Мониторинг</button>
        <button style={tabStyle(envTab === 'env')} onClick={() => setEnvTab('env')}>🔑 ENV</button>
        <button style={tabStyle(envTab === 'environments')} onClick={() => setEnvTab('environments')}>🌍 Environments</button>
        <button style={tabStyle(envTab === 'settings')} onClick={() => setEnvTab('settings')}>⚙ Настройки</button>
      </div>

      {/* Deploys tab */}
      {envTab === 'deploys' && (
        <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: '12px', overflowY: 'auto' }}>
          <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
            {project.repoUrl && (
              <button
                onClick={deployGit}
                disabled={deploying}
                style={{ padding: '8px 16px', background: 'var(--accent)', color: '#fff', border: 'none', borderRadius: '8px', fontSize: '13px', fontWeight: 700, cursor: deploying ? 'not-allowed' : 'pointer', opacity: deploying ? 0.7 : 1 }}
              >
                {deploying ? '⟳ Деплой...' : '🚀 Задеплоить из Git'}
              </button>
            )}
            <button
              onClick={() => fileRef.current?.click()}
              disabled={deploying}
              style={{ padding: '8px 16px', background: 'var(--bg-3)', border: '1px solid var(--border)', borderRadius: '8px', fontSize: '13px', fontWeight: 700, cursor: deploying ? 'not-allowed' : 'pointer', color: 'var(--text-secondary)' }}
            >
              📦 Загрузить ZIP
            </button>
            <input
              ref={fileRef}
              type="file"
              accept=".zip"
              style={{ display: 'none' }}
              onChange={e => { if (e.target.files?.[0]) deployZip(e.target.files[0]) }}
            />
            <button
              onClick={reload}
              style={{ padding: '8px 12px', background: 'var(--bg-3)', border: '1px solid var(--border)', borderRadius: '8px', fontSize: '12px', cursor: 'pointer', color: 'var(--text-muted)' }}
            >↻</button>
          </div>

          {/* Webhook hint */}
          <div style={{ padding: '10px 12px', background: 'var(--bg-2)', borderRadius: '8px', border: '1px solid var(--border)', fontSize: '11px', color: 'var(--text-muted)' }}>
            <span style={{ fontWeight: 700, color: 'var(--text-secondary)' }}>GitHub Webhook URL: </span>
            <code style={{ wordBreak: 'break-all', userSelect: 'all' }}>
              {(import.meta.env.VITE_API_URL ?? '') + `/api/hosting/webhook/${project.webhookToken}`}
            </code>
            <span style={{ display: 'block', marginTop: '3px' }}>Content type: application/json · Events: push</span>
          </div>

          {loadingDeploys ? (
            <div style={{ textAlign: 'center', padding: '30px', color: 'var(--text-muted)' }}>Загрузка...</div>
          ) : deployments.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '40px', color: 'var(--text-muted)', fontSize: '14px' }}>
              <div style={{ fontSize: '32px', marginBottom: '8px' }}>🚀</div>
              Нет деплоев. Запустите первый!
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
              {deployments.map(d => <DeploymentRow key={d.id} d={d} onShowLog={setLogTarget} />)}
            </div>
          )}
        </div>
      )}

      {/* ENV tab */}
      {envTab === 'env' && (
        <div style={{ flex: 1, overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: '10px' }}>
          <div style={{ fontSize: '12px', color: 'var(--text-muted)', padding: '8px 12px', background: 'var(--bg-2)', borderRadius: '8px', border: '1px solid var(--border)' }}>
            Переменные доступны во время сборки. Значения зашифрованы и никогда не отображаются.
          </div>
          {envKeys.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '24px', color: 'var(--text-muted)', fontSize: '13px' }}>Нет переменных</div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
              {envKeys.map(k => (
                <div key={k} style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '8px 12px', background: 'var(--bg-2)', border: '1px solid var(--border)', borderRadius: '8px' }}>
                  <code style={{ flex: 1, fontSize: '12px', color: 'var(--accent)' }}>{k}</code>
                  <span style={{ fontSize: '11px', color: 'var(--text-muted)' }}>••••••••</span>
                  <button onClick={() => removeEnvVar(k)} style={{ background: 'none', border: 'none', color: '#f87171', cursor: 'pointer', fontSize: '14px' }}>✕</button>
                </div>
              ))}
            </div>
          )}
          <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
            <input
              className="prop-input"
              placeholder="KEY_NAME"
              value={newEnvKey}
              onChange={e => setNewEnvKey(e.target.value.toUpperCase().replace(/[^A-Z0-9_]/g, ''))}
              style={{ flex: '1 1 120px', fontFamily: 'monospace', fontSize: '12px' }}
            />
            <input
              className="prop-input"
              placeholder="value"
              value={newEnvVal}
              onChange={e => setNewEnvVal(e.target.value)}
              type="password"
              style={{ flex: '2 1 180px', fontFamily: 'monospace', fontSize: '12px' }}
            />
            <button onClick={addEnvVar} style={{ padding: '6px 14px', background: 'var(--accent)', color: '#fff', border: 'none', borderRadius: '8px', fontSize: '12px', fontWeight: 700, cursor: 'pointer' }}>+ Добавить</button>
          </div>
        </div>
      )}

      {/* §17 Environments tab */}
      {envTab === 'environments' && (
        <EnvironmentsTab projectId={project.id} />
      )}

      {/* Settings tab */}
      {envTab === 'settings' && (
        <div style={{ flex: 1, overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: '10px' }}>
          {([
            { label: 'Название', key: 'name', placeholder: 'My Project' },
            { label: 'Git URL', key: 'repoUrl', placeholder: 'https://github.com/user/repo' },
            { label: 'Ветка', key: 'repoBranch', placeholder: 'main' },
            { label: 'Команда установки', key: 'installCmd', placeholder: 'npm install' },
            { label: 'Команда сборки', key: 'buildCmd', placeholder: 'npm run build' },
            { label: 'Директория вывода', key: 'outputDir', placeholder: 'dist' },
          ] as Array<{ label: string; key: keyof ApiHostingProject; placeholder: string }>).map(({ label, key, placeholder }) => (
            <div key={key}>
              <label style={{ display: 'block', fontSize: '10px', fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '4px' }}>{label}</label>
              <input
                className="prop-input"
                placeholder={placeholder}
                value={String(editingProject[key] ?? '')}
                onChange={e => setEditingProject(p => ({ ...p, [key]: e.target.value }))}
              />
            </div>
          ))}
          <div>
            <label style={{ display: 'block', fontSize: '10px', fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '4px' }}>Фреймворк</label>
            <select className="prop-select" value={editingProject.framework} onChange={e => setEditingProject(p => ({ ...p, framework: e.target.value }))}>
              {['auto','nextjs','react','vue','nuxt','astro','static','custom'].map(f => <option key={f} value={f}>{f}</option>)}
            </select>
          </div>
          <button onClick={saveSettings} disabled={saving} style={{ padding: '8px 16px', background: 'var(--accent)', color: '#fff', border: 'none', borderRadius: '8px', fontSize: '13px', fontWeight: 700, cursor: 'pointer', alignSelf: 'flex-start' }}>
            {saving ? 'Сохранение...' : 'Сохранить'}
          </button>
        </div>
      )}

      {/* Monitoring tab — §51 Observability */}
      {envTab === 'monitoring' && (
        <MonitoringTab projectId={project.id} />
      )}

      {logTarget && (
        <LogViewer
          deploymentId={logTarget.id}
          initialLog={logTarget.buildLog}
          status={logTarget.status}
          onClose={() => setLogTarget(null)}
        />
      )}
    </div>
  )
}

// ── Create project modal ──────────────────────────────────────────────────────

function CreateProjectModal({ onClose, onCreate }: { onClose: () => void; onCreate: (p: ApiHostingProject) => void }) {
  const [form, setForm] = useState({ name: '', repoUrl: '', repoBranch: 'main', buildCmd: 'npm run build', outputDir: 'dist', installCmd: 'npm install', framework: 'auto' })
  const [saving, setSaving] = useState(false)

  async function submit() {
    if (!form.name.trim()) return
    setSaving(true)
    try {
      const proj = await hostingApi.createProject({ ...form, repoUrl: form.repoUrl || undefined })
      onCreate(proj)
    } catch (e) { alert(String(e)) } finally { setSaving(false) }
  }

  const lStyle: React.CSSProperties = { display: 'block', fontSize: '10px', fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '4px' }

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 9000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '20px' }}>
      <div style={{ background: 'var(--bg-1)', borderRadius: '16px', padding: '28px', width: '100%', maxWidth: '500px', boxShadow: '0 24px 64px rgba(0,0,0,0.3)', border: '1px solid var(--border)' }}>
        <h2 style={{ margin: '0 0 20px', fontSize: '18px', fontWeight: 800, color: 'var(--text-primary)' }}>Новый проект</h2>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
          {([
            { label: 'Название *', key: 'name', placeholder: 'My App' },
            { label: 'Git URL (опционально)', key: 'repoUrl', placeholder: 'https://github.com/user/repo' },
            { label: 'Ветка', key: 'repoBranch', placeholder: 'main' },
            { label: 'Команда установки', key: 'installCmd', placeholder: 'npm install' },
            { label: 'Команда сборки', key: 'buildCmd', placeholder: 'npm run build' },
            { label: 'Директория вывода', key: 'outputDir', placeholder: 'dist' },
          ] as Array<{ label: string; key: keyof typeof form; placeholder: string }>).map(({ label, key, placeholder }) => (
            <div key={key}>
              <label style={lStyle}>{label}</label>
              <input className="prop-input" placeholder={placeholder} value={form[key]} onChange={e => setForm(f => ({ ...f, [key]: e.target.value }))} />
            </div>
          ))}
          <div>
            <label style={lStyle}>Фреймворк</label>
            <select className="prop-select" value={form.framework} onChange={e => setForm(f => ({ ...f, framework: e.target.value }))}>
              {['auto','nextjs','react','vue','nuxt','astro','static','custom'].map(x => <option key={x} value={x}>{x}</option>)}
            </select>
          </div>
          <div style={{ fontSize: '11px', color: 'var(--text-muted)', padding: '8px 10px', background: 'var(--bg-2)', borderRadius: '6px' }}>
            💡 Поддерживается статический вывод (SPA, Next.js export, Astro). Серверный SSR — в следующей версии.
          </div>
        </div>
        <div style={{ display: 'flex', gap: '8px', marginTop: '20px' }}>
          <button onClick={submit} disabled={saving || !form.name.trim()}
            style={{ flex: 1, padding: '10px', background: 'var(--accent)', color: '#fff', border: 'none', borderRadius: '10px', fontSize: '14px', fontWeight: 700, cursor: 'pointer', opacity: saving ? 0.7 : 1 }}>
            {saving ? 'Создание...' : 'Создать проект'}
          </button>
          <button onClick={onClose}
            style={{ padding: '10px 16px', background: 'var(--bg-3)', border: '1px solid var(--border)', borderRadius: '10px', fontSize: '14px', cursor: 'pointer', color: 'var(--text-muted)' }}>
            Отмена
          </button>
        </div>
      </div>
    </div>
  )
}

// ── Main panel ────────────────────────────────────────────────────────────────

export function HostingPanel() {
  const [projects, setProjects] = useState<ApiHostingProject[]>([])
  const [loading, setLoading] = useState(true)
  const [selected, setSelected] = useState<ApiHostingProject | null>(null)
  const [showCreate, setShowCreate] = useState(false)

  useEffect(() => {
    hostingApi.listProjects()
      .then(setProjects)
      .catch(() => null)
      .finally(() => setLoading(false))
  }, [])

  if (selected) {
    return (
      <div style={{ padding: '32px', maxWidth: '900px' }}>
        <ProjectDetail project={selected} onBack={() => setSelected(null)} />
      </div>
    )
  }

  return (
    <div style={{ padding: '32px', maxWidth: '900px' }}>
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: '24px' }}>
        <div>
          <h1 style={{ fontSize: '28px', fontWeight: 800, margin: '0 0 4px', color: 'var(--text-primary)' }}>Code Hosting</h1>
          <p style={{ fontSize: '14px', color: 'var(--text-muted)', margin: 0 }}>Задеплойте любой статический проект: React, Vue, Astro, Next.js (static export)</p>
        </div>
        <button
          onClick={() => setShowCreate(true)}
          style={{ padding: '10px 20px', background: 'var(--accent)', color: '#fff', border: 'none', borderRadius: '10px', fontSize: '14px', fontWeight: 700, cursor: 'pointer', whiteSpace: 'nowrap' }}
        >
          + Новый проект
        </button>
      </div>

      {loading ? (
        <div style={{ textAlign: 'center', padding: '80px', color: 'var(--text-muted)' }}>
          <div style={{ fontSize: '32px', marginBottom: '12px' }}>⟳</div>
          Загрузка...
        </div>
      ) : projects.length === 0 ? (
        <div style={{ textAlign: 'center', padding: '80px 40px' }}>
          <div style={{ fontSize: '56px', marginBottom: '16px' }}>🚀</div>
          <h2 style={{ fontSize: '20px', fontWeight: 800, margin: '0 0 8px', color: 'var(--text-primary)' }}>Нет проектов</h2>
          <p style={{ fontSize: '14px', color: 'var(--text-muted)', margin: '0 0 20px' }}>Создайте первый проект и задеплойте его за секунды</p>
          <button onClick={() => setShowCreate(true)}
            style={{ padding: '10px 24px', background: 'var(--accent)', color: '#fff', border: 'none', borderRadius: '10px', fontSize: '14px', fontWeight: 700, cursor: 'pointer' }}>
            Создать проект
          </button>
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
          {projects.map(proj => {
            const latest = proj.latestDeployment
            return (
              <div
                key={proj.id}
                onClick={() => setSelected(proj)}
                style={{ display: 'flex', alignItems: 'center', gap: '14px', padding: '16px 20px', background: 'var(--bg-2)', border: '1px solid var(--border)', borderRadius: '12px', cursor: 'pointer', transition: 'border-color 0.15s' }}
                onMouseEnter={e => (e.currentTarget.style.borderColor = 'var(--accent)')}
                onMouseLeave={e => (e.currentTarget.style.borderColor = 'var(--border)')}
              >
                <div style={{ fontSize: '28px' }}>🚀</div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: '16px', fontWeight: 700, color: 'var(--text-primary)', marginBottom: '2px' }}>{proj.name}</div>
                  <div style={{ fontSize: '12px', color: 'var(--text-muted)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {proj.repoUrl ?? 'Нет Git-репо'} · {proj.framework}
                  </div>
                </div>
                {latest && <StatusBadge status={latest.status} />}
                {latest?.previewUrl && latest.status === 'success' && (
                  <a
                    href={latest.previewUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={e => e.stopPropagation()}
                    style={{ fontSize: '12px', padding: '5px 12px', background: 'var(--accent)', color: '#fff', borderRadius: '8px', textDecoration: 'none', fontWeight: 700 }}
                  >
                    Preview
                  </a>
                )}
                <div style={{ fontSize: '11px', color: 'var(--text-muted)', whiteSpace: 'nowrap' }}>{proj._count?.deployments ?? 0} деплоев</div>
                <span style={{ fontSize: '18px', color: 'var(--text-muted)' }}>›</span>
              </div>
            )
          })}
        </div>
      )}

      {showCreate && (
        <CreateProjectModal
          onClose={() => setShowCreate(false)}
          onCreate={p => { setProjects(ps => [p, ...ps]); setShowCreate(false); setSelected(p) }}
        />
      )}
    </div>
  )
}
